import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FilePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { usePageTitle } from '@/components/layout/page-title';
import { PageHeader } from '@/components/layout/PageHeader';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { RefreshButton } from '@/features/invoices/RefreshButton';
import { useScrollToTop } from '@/features/invoices/useRefreshList';
import { DraftsCardList, DraftsTable } from './DraftTable';
import { useDeleteDraft, useDrafts, useRefreshDrafts, type DraftSummary } from './api';

export function DraftListPage() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const isMobile = useIsMobile();
  usePageTitle(t('invoices.heading.drafts'));

  const navigate = useNavigate();
  const drafts = useDrafts();
  const refresh = useRefreshDrafts();
  const { mutate: deleteDraft } = useDeleteDraft();
  // Refresh collapses the list back to page 1, so send the scroll position back up to match.
  const { rootRef, scrollToTop } = useScrollToTop();

  const items = useMemo(() => {
    const seen = new Set<number>();
    const flat: DraftSummary[] = [];
    for (const page of drafts.data?.pages ?? []) {
      for (const draft of page.items) {
        if (!seen.has(draft.id)) {
          seen.add(draft.id);
          flat.push(draft);
        }
      }
    }
    return flat;
  }, [drafts.data]);

  // Stable identities so the memoized rows don't all re-render when a page is appended.
  const onOpen = useCallback((id: number) => navigate(`/invoices/drafts/${id}`), [navigate]);
  const onDelete = useCallback(
    (draft: DraftSummary) =>
      deleteDraft(draft.id, {
        onSuccess: () => toast.success(t('invoices.drafts.toasts.deleted')),
        onError: () => toast.error(t('invoices.drafts.toasts.delete_failed')),
      }),
    [deleteDraft, t],
  );

  const loadMore = {
    hasMore: drafts.hasNextPage,
    isLoadingMore: drafts.isFetchingNextPage,
    onLoadMore: drafts.fetchNextPage,
  };

  return (
    <div ref={rootRef} className="flex h-full min-h-0 flex-col gap-6">
      <PageHeader
        title={t('invoices.drafts.title')}
        description={t('invoices.drafts.description')}
        actions={
          <>
            <RefreshButton {...refresh} onClick={scrollToTop} />
            <Button type="button" onClick={() => navigate('/invoices/new')}>
              <FilePlus data-icon="inline-start" />
              {t('invoices.drafts.new')}
            </Button>
          </>
        }
      />

      {drafts.isPending ? (
        <Skeleton className={isMobile ? 'h-64 w-full' : 'min-h-0 w-full flex-1'} />
      ) : drafts.isError ? (
        <MessageCard isMobile={isMobile} tone="destructive">
          {t('invoices.drafts.load_error')}
        </MessageCard>
      ) : items.length === 0 ? (
        <MessageCard isMobile={isMobile}>
          <p className="text-sm">{t('invoices.drafts.empty')}</p>
          <p className="max-w-sm text-xs">{t('invoices.drafts.empty_hint')}</p>
        </MessageCard>
      ) : isMobile ? (
        // The header above stays put; the card list fills the remaining height and scrolls inside its
        // own region, so the page never grows a native scrollbar — matching the desktop table.
        <Card className="min-h-0 flex-1 py-0">
          <CardContent className="flex min-h-0 flex-1 flex-col p-0">
            <ScrollArea className="cn-vertical-scroll min-h-0 flex-1">
              <DraftsCardList
                items={items}
                lng={lng}
                onOpen={onOpen}
                onDelete={onDelete}
                {...loadMore}
              />
            </ScrollArea>
          </CardContent>
        </Card>
      ) : (
        // Desktop: the table fills the remaining viewport height and scrolls inside its own ScrollArea,
        // so the page never grows a native scrollbar.
        <Card className="min-h-0 flex-1">
          <CardContent className="flex min-h-0 flex-1 flex-col">
            <DraftsTable
              items={items}
              lng={lng}
              onOpen={onOpen}
              onDelete={onDelete}
              {...loadMore}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * A status message (empty / error) that occupies the same height as the populated list on desktop, so
 * the container size never changes with content. Stays natural-flow on mobile, where the page scrolls.
 */
function MessageCard({
  isMobile,
  tone = 'muted',
  children,
}: {
  isMobile: boolean;
  tone?: 'muted' | 'destructive';
  children: ReactNode;
}) {
  return (
    <Card className={isMobile ? undefined : 'min-h-0 flex-1'}>
      <CardContent
        className={cn(
          'flex flex-col items-center gap-2 py-6 text-center text-sm',
          tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground',
          isMobile ? undefined : 'min-h-0 flex-1 justify-center',
        )}
      >
        {children}
      </CardContent>
    </Card>
  );
}
